import { Component } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';

@Component({
	selector: 'app-modal-billing-upgrade-account',
	templateUrl: './modal-billing-upgrade-account.component.html'
})
export class ModalBillingUpgradeAccountComponent extends UIModalBaseComponent {

	disabled = false;
	currentPackage = null;
	selectedPackage = null;
	data;

	// Off by default: this modal is also hosted by the quota-limit blocker, which is no
	// place to offer cancellation. The billing page opts in.
	allowCancellation = false;

	// The customer's cancellation request. The reason and comments go straight through
	// to the CRM and the internal staff email, so the wording here is what they see.
	readonly cancellationReasons = ['Too expensive', 'Not using it enough', 'Missing features', 'Switching to another product', 'Business closing or changing', 'Other'];
	showCancellationForm = false;
	cancellationReason = '';
	cancellationFeedback = '';

	constructor(
		public app: AppService,
		private api: ApiService
	) {
		super();
	}

	// Package changes are blocked while money is owed - settle up first. Cancellation
	// stays available, which is why the modal itself opens regardless. Mirrored by the
	// same guard in the upgrade_account endpoint; this only hides the controls.
	get isInArrears(): boolean {
		return !!this.data && this.data.outstanding > 50;
	}

	get canRequestCancellation(): boolean {
		return this.allowCancellation && this.app.orgInfo?.status === 'paid';
	}

	// WeQuote staff only. Gated on allowCancellation for the same reason as the
	// customer request above - the quota blocker is no place to cancel from.
	private get isStaffCancellation(): boolean {
		return !!this.data && this.allowCancellation && !!this.app.orgInfo?.role.is_root;
	}

	get canCancelAccount(): boolean {
		return this.isStaffCancellation && this.app.orgInfo?.status === 'paid';
	}

	// Already scheduled to cancel - offer to call it off instead.
	get canResumeAccount(): boolean {
		return this.isStaffCancellation && this.app.orgInfo?.status === 'pending_cancellation';
	}

	open(mdata: any = null) {
		this.allowCancellation = !!mdata?.allowCancellation;
		this.showCancellationForm = false;
		this.cancellationReason = '';
		this.cancellationFeedback = '';

		this.api.account.listPackages(result => {
			this.data = result;

			this.data.packages.forEach(p => {
				if (p.is_current) this.currentPackage = p;
			});
			this.selectedPackage = this.currentPackage;
			this.modal.open();
		}, error => {
			this.app.notifications.showDanger(error.message);
		});
	}

	requestCancellation() {
		if (!this.cancellationReason) return;

		this.disabled = true;
		this.api.account.requestCancellation({ reason: this.cancellationReason, feedback: this.cancellationFeedback }, () => {
			this.disabled = false;
			this.app.notifications.showSuccess('Cancellation requested.', 'Our team will be in touch shortly.');
			this.modal.close();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	cancelAccount() {
		if (!this.data) return;

		// An account with money owing is cancelled on the spot; a paid-up one keeps
		// running until the end of the period it has already paid for. The first is
		// destructive and irreversible, so it gets spelled out in full.
		let content: string;
		let confirmLabel: string;

		if (this.isInArrears) {
			content = '<p class="text-bold text-danger">This account owes money, so it will be cancelled right now. This cannot be undone.</p>';
			content += '<p>The moment you confirm, all of the following happens to <b>' + this.app.orgInfo?.company_name + '</b>:</p>';
			content += '<ul>';
			content += '<li>Every unpaid invoice is <b>voided</b>, here and in Xero. The money owed is written off and will not be recoverable.</li>';
			content += '<li>Their saved payment card is <b>deleted</b> from Stripe.</li>';
			content += '<li>Their quota is <b>wiped</b>.</li>';
			content += '<li>Billing stops and no further invoices are ever issued.</li>';
			content += '<li>Every user is <b>locked out</b> of the account immediately.</li>';
			content += '</ul>';
			content += '<p>Their data is retained, but getting them back requires a full re-activation and a new subscription. There is no undo button.</p>';
			content += '<p class="text-bold">Are you certain you want to cancel this account right now?</p>';
			confirmLabel = '-Cancel Account Immediately';
		} else {
			content = '<p>This account is paid up, so it will stay active until the end of the current billing period and then be cancelled automatically.</p>';
			content += '<p>Nothing changes for them today, and you can call this off at any point before then.</p>';
			content += '<p class="text-bold">Schedule this account for cancellation?</p>';
			confirmLabel = '!Schedule Cancellation';
		}

		this.app.dialog.popHtml('Cancel Account', content, ['Cancel', confirmLabel], btn => {
			if (btn !== 1) return;

			this.disabled = true;
			this.api.account.cancelAccount(result => {
				this.disabled = false;

				if (result.status === 'cancelled') {
					this.app.notifications.showSuccess('Account cancelled.');
				} else {
					this.app.notifications.showSuccess('Account scheduled for cancellation.');
				}

				// Xero can refuse a void even though the local cancellation went through.
				// Say so rather than letting the two quietly disagree.
				(result.warnings || []).forEach(warning => this.app.notifications.showWarning(warning));

				// Closing with a truthy payload makes the billing page reload its data.
				this.api.refreshUserInfo(() => this.modal.close(true));
			}, error => {
				this.disabled = false;
				this.app.notifications.showDanger(error.message);
			});
		});
	}

	resumeAccount() {
		this.app.dialog.pop(
			'Keep Subscription',
			'This will call off the scheduled cancellation and put the account back on its subscription. Billing carries on as normal from their next billing date. Continue?',
			['Cancel', '*Keep Subscription'],
			btn => {
				if (btn !== 1) return;

				this.disabled = true;
				this.api.account.resumeAccount(() => {
					this.disabled = false;
					this.app.notifications.showSuccess('Cancellation called off.', 'The account is back on its subscription.');
					this.api.refreshUserInfo(() => this.modal.close(true));
				}, error => {
					this.disabled = false;
					this.app.notifications.showDanger(error.message);
				});
			}
		);
	}

	upgradeButton() {
		this.disabled = true;
		this.api.account.upgradeAccount(this.selectedPackage.id, () => {
			this.disabled = false;
			this.currentPackage = this.selectedPackage;
			this.modal.close(true);
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

}
